/**
 * ORG LOYALTY PORTAL, the clinic's own screen.
 *
 * Top to bottom: a four-line checklist that gets the front desk working, the
 * one switch that changes how points behave, then whoever is waiting for a
 * decision. Everything else is folded away until it is asked for.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import PortalView from './PortalView';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { amount as amountOnly, money, moneyFieldProps, plain } from '../../lib/money';

const field = "w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm "
    + "dark:border-slate-700 dark:bg-slate-900";
const hint = "mt-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400";

const Step = ({ done, label, action, onAction, testid }) => (
    <li className="flex items-center gap-3 py-1.5" data-testid={testid}>
        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border
                          ${done ? 'border-emerald-500 bg-emerald-500 text-white'
                                 : 'border-slate-300 dark:border-slate-600'}`}
              data-testid={`${testid}-tick`}>
            {done ? <Check className="h-3 w-3" /> : null}
        </span>
        <span className={`text-sm ${done ? 'line-through opacity-60' : ''}`}>{label}</span>
        {!done && (
            <Button size="sm" className="ml-auto" data-testid={`${testid}-btn`} onClick={onAction}>
                {action}
            </Button>
        )}
    </li>
);

const Switch = ({ checked, onChange, label, note, testid }) => (
    <label className="block" data-testid={`${testid}-row`}>
        <span className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={checked} data-testid={testid}
                   onChange={(e) => onChange(e.target.checked)} />
            {label}
        </span>
        <span className={`${hint} block pl-6`}>{note}</span>
    </label>
);

export const DeskPanel = ({ orgId, cfg, onSave, busy, onDesign }) => {
    const { t } = useTranslation();
    const [row, setRow] = useState(null);
    const [queue, setQueue] = useState(null);
    const [draft, setDraft] = useState({ first_name: '', email: '', role: 'staff' });
    const [working, setWorking] = useState(false);
    const [open, setOpen] = useState(null);
    const [draftAmount, setDraftAmount] = useState('');
    const [why, setWhy] = useState('');
    const [confirmOff, setConfirmOff] = useState(false);
    const [saved, setSaved] = useState('');
    const [problem, setProblem] = useState('');
    const [cards, setCards] = useState([]);
    const [picking, setPicking] = useState(false);
    const [previewScreen, setPreviewScreen] = useState('desk_home');
    const [maxBill, setMaxBill] = useState(
        cfg?.desk_max_bill == null ? '' : amountOnly(cfg.desk_max_bill));
    const rateRef = useRef(null);
    const staffRef = useRef(null);

    const on = !!cfg?.switches?.desk;
    // What the clinic has just tapped, so the screen answers at once and does
    // not wait for the settings to come back.
    const [justSet, setJustSet] = useState({});
    const isOn = (key) => (key in justSet ? justSet[key] : !!cfg?.switches?.[key]);
    const holding = !!cfg?.switches?.desk_approval;
    const rate = Number(cfg?.points_per_dollar || 0);

    const load = useCallback(() => Promise.all([
        api.get('/loyalty/staff', { params: { organization_id: orgId } })
            .then((r) => setRow(r.data)).catch(() => setRow(null)),
        api.get('/loyalty/desk/pending', { params: { organization_id: orgId } })
            .then((r) => setQueue(r.data)).catch(() => setQueue(null)),
        api.get('/cards', { params: { organization_id: orgId } })
            .then((r) => setCards(Array.isArray(r.data) ? r.data : (r.data?.cards || [])))
            .catch(() => setCards([])),
    ]), [orgId]);
    useEffect(() => { load(); }, [load]);

    const plainProblem = (code) => ({
        points_spent: t('loyalty.desk.pointsSpent', 'Some of these points are already spent'),
        not_desk: t('loyalty.desk.undoNotDesk', 'This card was not used at the front desk'),
        too_old: t('loyalty.desk.undoTooOld', 'This was more than a day ago, so it cannot be undone'),
        email_in_use: t('loyalty.desk.emailInUse', 'Someone working here already uses that email'),
    }[code] || t('loyalty.desk.tryAgain', 'That did not work. Please try again.'));

    const act = async (fn) => {
        setWorking(true);
        setProblem('');
        try { await fn(); await load(); }
        catch (e) { setProblem(plainProblem(e?.response?.data?.detail)); }
        finally { setWorking(false); }
    };
    const reveal = (ref) => {
        window.setTimeout(() => {
            ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            ref.current?.focus?.();
        }, 120);
    };

    const perm = (key) => !!row?.permissions?.[key];
    const setPerm = (key, value) => act(() => onSave({
        desk_permissions: { ...(row?.permissions || {}), [key]: value } }));

    const setMode = (card, mode) => act(() => api.put(`/cards/${card.id}/landing-settings`, {
        landing_page_enabled: !!card.landing_page_enabled,
        interaction_mode: mode,
    }));
    const attached = cards.filter((c) => c.interaction_mode === 'loyalty_desk');
    const free = cards.filter((c) => c.interaction_mode !== 'loyalty_desk');

    const PERMS = [
        ['staff_add_points', t('loyalty.desk.permPoints', 'Staff may add points for a bill'),
            t('loyalty.desk.permPointsNote',
                'Your receptionist can type what the client spent and give the points.')],
        ['staff_see_checkins', t('loyalty.desk.permCheckins', 'Staff may see who is waiting'),
            t('loyalty.desk.permCheckinsNote',
                'The list of clients who signed in on the card today.')],
        ['staff_redeem_cards', t('loyalty.desk.permRedeem', 'Staff may use a gift card'),
            t('loyalty.desk.permRedeemNote',
                'They can scan or type a gift card code and mark it used.')],
        ['manager_approve', t('loyalty.desk.permApprove', 'Managers may approve points'),
            t('loyalty.desk.permApproveNote',
                'Only needed when points are waiting for approval.')],
        ['manager_override', t('loyalty.desk.permOverride', 'Managers may change the amount'),
            t('loyalty.desk.permOverrideNote',
                'They can correct what was typed before saying yes. A reason is always asked.')],
        ['manager_expiry', t('loyalty.desk.permExpiry', 'Managers may change how long a visit stays open'),
            t('loyalty.desk.permExpiryNote',
                'After this many hours, a client who signed in drops off the list.')],
        ['manager_staff_awards', t('loyalty.desk.permAwards', 'Managers may see what each person gave'),
            t('loyalty.desk.permAwardsNote', "Today's points, named by who added them.")],
        ['limit_one_per_day', t('loyalty.desk.permOnePerDay', 'One go per client per day'),
            t('loyalty.desk.permOnePerDayNote',
                'The same client cannot be given points twice in one day.')],
        ['limit_max_bill', t('loyalty.desk.permMaxBill', 'Put a ceiling on one bill'),
            t('loyalty.desk.permMaxBillNote',
                'Anything above the amount you set below is refused.')],
    ];

    return (
        <div className="space-y-5" data-testid="cc-loyalty-desk">
            {/* 0. WHAT IT LOOKS LIKE ON A PHONE, in this clinic's own design. */}
            <section className="rounded-xl border border-slate-200 p-3 dark:border-slate-700"
                     data-testid="cc-desk-preview-section">
                <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold">
                        {t('loyalty.desk.previewTitle', 'What your front desk looks like')}
                    </p>
                    <Button size="sm" variant="outline" className="ml-auto"
                            data-testid="cc-desk-design-screens"
                            onClick={() => onDesign?.(previewScreen)}>
                        {t('loyalty.desk.designScreens', 'Design these screens')}
                    </Button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1" data-testid="cc-desk-preview-tabs">
                    {[['desk_home', t('loyalty.screen.desk_home', 'Welcome')],
                      ['desk_staff_signin', t('loyalty.screen.desk_staff_signin', 'Team sign in')],
                      ['desk_staff', t('loyalty.screen.desk_staff', 'Check-ins')],
                      ['desk_checked_in', t('loyalty.screen.desk_checked_in', 'Checked in')],
                      ['desk_award', t('loyalty.screen.desk_award', 'Add points')],
                      ['desk_manager', t('loyalty.screen.desk_manager', 'Management')],
                      ['desk_decide', t('loyalty.screen.desk_decide', 'Decide')]].map(([id, label]) => (
                        <button key={id} type="button" data-testid={`cc-desk-preview-tab-${id}`}
                                onClick={() => setPreviewScreen(id)}
                                className={`rounded-full px-2.5 py-1 text-[11px] ${previewScreen === id
                                    ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                                    : 'bg-slate-100 dark:bg-slate-800'}`}>
                            {label}
                        </button>
                    ))}
                </div>
                <div className="mt-3 flex justify-center" data-testid="cc-desk-preview">
                    <PortalView screen={previewScreen} cfg={cfg} preview framed />
                </div>
            </section>

            {/* 1. THE CHECKLIST — four things, each one undoable from here. */}
            {problem && (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700
                              dark:bg-amber-500/10 dark:text-amber-300"
                   data-testid="cc-desk-problem">
                    {problem}
                </p>
            )}
            <section className="rounded-xl border border-slate-200 p-3 dark:border-slate-700"
                     data-testid="cc-desk-checklist">
                <p className="text-sm font-semibold">
                    {t('loyalty.desk.setupTitle', 'Get your front desk working')}
                </p>
                <ul className="mt-1 divide-y divide-slate-100 dark:divide-slate-800">
                    {/* ON / OFF, both ways, any time. */}
                    <li className="flex flex-wrap items-center gap-3 py-2" data-testid="cc-desk-step-on">
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border
                                          ${on ? 'border-emerald-500 bg-emerald-500 text-white'
                                               : 'border-slate-300 dark:border-slate-600'}`}
                              data-testid="cc-desk-step-on-tick">
                            {on ? <Check className="h-3 w-3" /> : null}
                        </span>
                        <span className="text-sm">
                            {on ? t('loyalty.desk.deskOn', 'Front desk: ON')
                                : t('loyalty.desk.deskOff', 'Front desk: OFF')}
                        </span>
                        {confirmOff ? (
                            <span className="ml-auto flex flex-wrap items-center gap-2">
                                <span className={hint}>
                                    {t('loyalty.desk.offAsk',
                                        'The card stops working. Your staff, settings and all points stay.')}
                                </span>
                                <Button size="sm" variant="outline" disabled={working}
                                        data-testid="cc-desk-step-on-off-go"
                                        onClick={() => act(async () => {
                                            await onSave({ desk: false }); setConfirmOff(false);
                                        })}>
                                    {t('loyalty.desk.turnOff', 'Turn off')}
                                </Button>
                                <Button size="sm" variant="ghost" data-testid="cc-desk-step-on-off-cancel"
                                        onClick={() => setConfirmOff(false)}>
                                    {t('common.cancel', 'Cancel')}
                                </Button>
                            </span>
                        ) : (
                            <Button size="sm" variant={on ? 'outline' : 'default'} className="ml-auto"
                                    disabled={working} data-testid="cc-desk-step-on-btn"
                                    onClick={() => (on ? setConfirmOff(true)
                                        : act(() => onSave({ desk: true })))}>
                                {on ? t('loyalty.desk.turnOff', 'Turn off')
                                    : t('loyalty.desk.stepTurnOn', 'Turn on')}
                            </Button>
                        )}
                    </li>

                    {/* POINTS PER DOLLAR — set it, or clear it. */}
                    <li className="flex items-center gap-3 py-2" data-testid="cc-desk-step-rate">
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border
                                          ${rate > 0 ? 'border-emerald-500 bg-emerald-500 text-white'
                                                     : 'border-slate-300 dark:border-slate-600'}`}
                              data-testid="cc-desk-step-rate-tick">
                            {rate > 0 ? <Check className="h-3 w-3" /> : null}
                        </span>
                        <span className="text-sm">
                            {rate > 0
                                ? t('loyalty.desk.rateIs', '{{rate}} points per dollar', { rate })
                                : t('loyalty.desk.step2', 'Say how many points per dollar')}
                        </span>
                        <Button size="sm" variant="outline" className="ml-auto" disabled={working}
                                data-testid="cc-desk-step-rate-btn" onClick={() => reveal(rateRef)}>
                            {rate > 0 ? t('common.change', 'Change') : t('loyalty.desk.stepSet', 'Set it')}
                        </Button>
                        {rate > 0 && (
                            <Button size="sm" variant="ghost" disabled={working}
                                    data-testid="cc-desk-step-rate-clear"
                                    onClick={() => (open === 'clear-rate'
                                        ? act(async () => { await onSave({ points_per_dollar: '' });
                                            setOpen(null); })
                                        : setOpen('clear-rate'))}>
                                {open === 'clear-rate' ? t('loyalty.desk.clearAsk', 'Clear it?')
                                    : t('common.clear', 'Clear')}
                            </Button>
                        )}
                    </li>

                    {/* STAFF — invite, or remove in the list below. */}
                    <li className="flex items-center gap-3 py-2" data-testid="cc-desk-step-staff">
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border
                                          ${(row?.staff || []).length ? 'border-emerald-500 bg-emerald-500 text-white'
                                                                      : 'border-slate-300 dark:border-slate-600'}`}
                              data-testid="cc-desk-step-staff-tick">
                            {(row?.staff || []).length ? <Check className="h-3 w-3" /> : null}
                        </span>
                        <span className="text-sm">
                            {(row?.staff || []).length
                                ? t('loyalty.desk.staffCount', '{{count}} people can sign in',
                                    { count: (row.staff || []).length })
                                : t('loyalty.desk.step3', 'Add your staff')}
                        </span>
                        <Button size="sm" variant="outline" className="ml-auto"
                                data-testid="cc-desk-step-staff-btn" onClick={() => reveal(staffRef)}>
                            {(row?.staff || []).length ? t('common.manage', 'Manage')
                                : t('loyalty.desk.stepInvite', 'Invite')}
                        </Button>
                    </li>

                    {/* THE CARD — attach one, or detach it. */}
                    <li className="py-2" data-testid="cc-desk-step-card">
                        <div className="flex flex-wrap items-center gap-3">
                            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border
                                              ${attached.length ? 'border-emerald-500 bg-emerald-500 text-white'
                                                                : 'border-slate-300 dark:border-slate-600'}`}
                                  data-testid="cc-desk-step-card-tick">
                                {attached.length ? <Check className="h-3 w-3" /> : null}
                            </span>
                            <span className="text-sm">
                                {attached.length
                                    ? t('loyalty.desk.cardAttached', 'Card {{card}} is attached',
                                        { card: attached.map((c) => c.card_id).join(', ') })
                                    : t('loyalty.desk.step4', 'Attach a card')}
                            </span>
                            {attached.length === 0 ? (
                                <Button size="sm" className="ml-auto" data-testid="cc-desk-step-card-btn"
                                        onClick={() => setPicking(!picking)}>
                                    {t('loyalty.desk.attachCard', 'Attach a card')}
                                </Button>
                            ) : (
                                <span className="ml-auto flex flex-wrap gap-2">
                                    <Button size="sm" variant="outline" data-testid="cc-desk-open-device"
                                            onClick={() => window.open(
                                                `/loyalty-desk/${attached[0].card_id}`, '_blank', 'noopener')}>
                                        {t('loyalty.desk.openHere', 'Open the front desk on this device')}
                                    </Button>
                                    <Button size="sm" variant="ghost" disabled={working}
                                            data-testid="cc-desk-detach"
                                            onClick={() => (open === 'detach'
                                                ? (setOpen(null), setMode(attached[0],
                                                    row?.prev_modes?.[attached[0].card_id] || 'standard'))
                                                : setOpen('detach'))}>
                                        {open === 'detach' ? t('loyalty.desk.detachAsk', 'Detach it?')
                                            : t('loyalty.desk.detach', 'Detach card')}
                                    </Button>
                                </span>
                            )}
                        </div>
                        {picking && attached.length === 0 && (
                            <ul className="mt-2 space-y-1" data-testid="cc-desk-card-pick">
                                {free.length === 0 && (
                                    <li className={hint} data-testid="cc-desk-no-free-card">
                                        {t('loyalty.desk.noFreeCard',
                                            'This clinic has no card yet. Create one in Cards first.')}
                                    </li>
                                )}
                                {free.slice(0, 8).map((c) => (
                                    <li key={c.id} className="flex items-center gap-2 text-xs"
                                        data-testid={`cc-desk-card-opt-${c.card_id}`}>
                                        <span className="font-medium">{c.card_id}</span>
                                        <span className="opacity-70">{c.name}</span>
                                        <Button size="sm" variant="outline" className="ml-auto"
                                                disabled={working}
                                                data-testid={`cc-desk-card-use-${c.card_id}`}
                                                onClick={() => { setPicking(false);
                                                    onSave({ desk_prev_modes: {
                                                        ...(row?.prev_modes || {}),
                                                        [c.card_id]: c.interaction_mode || 'standard' } });
                                                    setMode(c, 'loyalty_desk'); }}>
                                            {t('loyalty.desk.useThis', 'Use this one')}
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </li>
                </ul>
            </section>

            {/* 2. THE ONE SWITCH THAT CHANGES HOW POINTS BEHAVE. */}
            <Switch testid="cc-desk-approval-switch" checked={holding}
                    onChange={(v) => act(() => onSave({ desk_approval: v }))}
                    label={t('loyalty.cc.switch.desk_approval', 'Manager approves points')}
                    note={t('loyalty.desk.approvalNote',
                        'Points your staff add wait until a manager says yes. '
                        + 'Leave it off and points go straight to the client.')} />

            {/* 3. WHOEVER IS WAITING. One tap says yes. */}
            <section className="rounded-xl border border-slate-200 p-3 dark:border-slate-700"
                     data-testid="cc-desk-pending-section">
                <p className="text-sm font-semibold">
                    {t('loyalty.desk.pendingTitle', 'Waiting for approval')}
                    {(queue?.count || 0) > 0 && (
                        <span className="ml-2 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] text-white"
                              data-testid="cc-desk-pending-count">{queue.count}</span>
                    )}
                </p>
                {(queue?.pending || []).length === 0 ? (
                    <p className={hint} data-testid="cc-desk-no-pending">
                        {t('loyalty.desk.noPending', 'Nothing is waiting.')}
                    </p>
                ) : (
                    <ul className="mt-2 space-y-2" data-testid="cc-desk-pending-list">
                        {(queue.pending || []).map((line) => (
                            <li key={line.id} className="rounded-lg border border-slate-200 px-3 py-2
                                                         text-xs dark:border-slate-700"
                                data-testid={`cc-desk-pending-${line.id}`}>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-medium">{line.member_name}</span>
                                    <span className="opacity-70">
                                        {`${money(line.bill_amount)} · `}
                                        {`${Number(line.points).toLocaleString()} `}
                                        {t('loyalty.points', 'points')}
                                        {` · ${line.staff_name}`}
                                    </span>
                                    <Button size="sm" className="ml-auto" disabled={working}
                                            data-testid={`cc-desk-approve-${line.id}`}
                                            onClick={() => act(() => api.post('/loyalty/desk/decide', {
                                                award_id: line.id, decision: 'approve',
                                            }, { params: { organization_id: orgId } }))}>
                                        {t('loyalty.desk.approve', 'Approve')}
                                    </Button>
                                    <button type="button" className="text-[11px] underline opacity-70"
                                            data-testid={`cc-desk-open-${line.id}`}
                                            onClick={() => { setOpen(open === line.id ? null : line.id);
                                                setWhy(''); setDraftAmount(amountOnly(line.bill_amount)); }}>
                                        {t('loyalty.desk.changeOrRefuse', 'Change or refuse')}
                                    </button>
                                </div>
                                {open === line.id && (
                                    <div className="mt-2 grid gap-2 sm:grid-cols-3">
                                        <Input data-testid={`cc-desk-amount-${line.id}`}
                                               {...moneyFieldProps(draftAmount, setDraftAmount)} />
                                        <Input placeholder={t('loyalty.desk.reasonLabel', 'Reason')}
                                               value={why} data-testid={`cc-desk-reason-${line.id}`}
                                               onChange={(e) => setWhy(e.target.value)} />
                                        <div className="flex gap-2">
                                            <Button size="sm" disabled={working}
                                                    data-testid={`cc-desk-change-approve-${line.id}`}
                                                    onClick={() => act(async () => {
                                                        await api.post('/loyalty/desk/decide', {
                                                            award_id: line.id, decision: 'approve',
                                                            amount: plain(draftAmount) === ''
                                                                || Number(plain(draftAmount)) === Number(line.bill_amount)
                                                                ? null : Number(plain(draftAmount)),
                                                            reason: why,
                                                        }, { params: { organization_id: orgId } });
                                                        setOpen(null); setWhy(''); setDraftAmount('');
                                                    })}>
                                                {t('loyalty.desk.approve', 'Approve')}
                                            </Button>
                                            <Button size="sm" variant="outline" disabled={working}
                                                    data-testid={`cc-desk-reject-${line.id}`}
                                                    onClick={() => act(async () => {
                                                        await api.post('/loyalty/desk/decide', {
                                                            award_id: line.id, decision: 'reject',
                                                            reason: why,
                                                        }, { params: { organization_id: orgId } });
                                                        setOpen(null); setWhy(''); setDraftAmount('');
                                                    })}>
                                                {t('loyalty.desk.reject', 'Not added')}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* EVERY AWARD AS ITS OWN LINE, so one of them can be put right. */}
            <section className="rounded-xl border border-slate-200 p-3 dark:border-slate-700"
                     data-testid="cc-desk-lines-section">
                <p className="text-sm font-semibold">
                    {t('loyalty.desk.awardsTitle', 'Points added today')}
                </p>
                {(queue?.lines || []).length === 0 ? (
                    <p className={hint} data-testid="cc-desk-no-lines">
                        {t('loyalty.desk.noAwards', 'No points added today.')}
                    </p>
                ) : (
                    <ul className="mt-2 space-y-2" data-testid="cc-desk-lines">
                        {(queue.lines || []).map((line) => (
                            <li key={line.id} className="rounded-lg border border-slate-200 px-3 py-2
                                                         text-xs dark:border-slate-700"
                                data-testid={`cc-desk-line-${line.id}`}>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-medium">{line.staff_name || '—'}</span>
                                    <span className="opacity-70">
                                        {`${line.member_name} · ${money(line.bill_amount)} · `}
                                        {`${Number(line.points).toLocaleString()} `}
                                        {t('loyalty.points', 'points')}
                                        {` · ${t(`loyalty.desk.status_${line.status}`, line.status)}`}
                                    </span>
                                    {line.status === 'approved' && (
                                        <Button size="sm" variant="outline" className="ml-auto"
                                                data-testid={`cc-desk-line-open-${line.id}`}
                                                onClick={() => { setOpen(open === line.id ? null : line.id);
                                                    setWhy(''); setDraftAmount(amountOnly(line.bill_amount)); }}>
                                            {t('loyalty.desk.changeAmount', 'Change amount')}
                                        </Button>
                                    )}
                                    {line.status === 'rejected' && (
                                        <Button size="sm" variant="outline" className="ml-auto"
                                                disabled={working}
                                                data-testid={`cc-desk-reconsider-${line.id}`}
                                                onClick={() => act(() => api.post('/loyalty/desk/decide', {
                                                    award_id: line.id, decision: 'reconsider',
                                                    reason: t('loyalty.desk.reconsider', 'Reconsider'),
                                                }, { params: { organization_id: orgId } }))}>
                                            {t('loyalty.desk.reconsider', 'Reconsider')}
                                        </Button>
                                    )}
                                </div>
                                {open === line.id && line.status === 'approved' && (
                                    <div className="mt-2 grid gap-2 sm:grid-cols-3">
                                        <Label className="mb-1 block text-xs">
                                            {t('loyalty.desk.newAmount',
                                                'Corrected total bill amount today (before taxes)')}
                                        </Label>
                                        <Input data-testid={`cc-desk-line-amount-${line.id}`}
                                               placeholder={t('loyalty.desk.newAmount',
                                                   'Corrected total bill amount today (before taxes)')}
                                               {...moneyFieldProps(draftAmount, setDraftAmount)} />
                                        <Input placeholder={t('loyalty.desk.reasonLabel', 'Reason')}
                                               value={why} data-testid={`cc-desk-line-reason-${line.id}`}
                                               onChange={(e) => setWhy(e.target.value)} />
                                        <div className="flex gap-2">
                                            <Button size="sm" disabled={working || !why}
                                                    data-testid={`cc-desk-line-correct-${line.id}`}
                                                    onClick={() => act(async () => {
                                                        await api.post('/loyalty/desk/correct', {
                                                            award_id: line.id,
                                                            amount: Number(plain(draftAmount)),
                                                            reason: why,
                                                        }, { params: { organization_id: orgId } });
                                                        setOpen(null); setWhy('');
                                                    })}>
                                                {t('common.save', 'Save')}
                                            </Button>
                                            <Button size="sm" variant="outline" disabled={working || !why}
                                                    data-testid={`cc-desk-line-takeback-${line.id}`}
                                                    onClick={() => act(async () => {
                                                        await api.post('/loyalty/desk/take-back', {
                                                            award_id: line.id, reason: why,
                                                        }, { params: { organization_id: orgId } });
                                                        setOpen(null); setWhy('');
                                                    })}>
                                                {t('loyalty.desk.takeBack', 'Take back')}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}

                {(queue?.redeemed || []).length > 0 && (
                    <div className="mt-3" data-testid="cc-desk-redeemed-section">
                        <p className="text-xs font-semibold">
                            {t('loyalty.desk.usedCards', 'Gift cards used today')}
                        </p>
                        {(queue.redeemed || []).map((card) => (
                            <p key={card.code} className="mt-1 flex items-center gap-2 text-xs"
                               data-testid={`cc-desk-used-${card.code}`}>
                                <span>
                                    {t('loyalty.desk.usedLine', 'Used — {{value}}',
                                        { value: money(card.value) })}
                                </span>
                                <span className="opacity-60">{card.code}</span>
                                <Button size="sm" variant="outline" className="ml-auto"
                                        disabled={working} data-testid={`cc-desk-undo-used-${card.code}`}
                                        onClick={() => act(() => api.post('/loyalty/desk/undo-redeem',
                                            { code: card.code },
                                            { params: { organization_id: orgId } }))}>
                                    {t('common.undo', 'Undo')}
                                </Button>
                            </p>
                        ))}
                    </div>
                )}
            </section>

            {/* 4. EVERYTHING ELSE, on the same page, nothing folded. */}
            <div className="space-y-5" data-testid="cc-desk-more-panel">
                    <section className="rounded-xl border border-slate-200 p-3 dark:border-slate-700"
                             data-testid="cc-desk-staff-section" ref={staffRef} tabIndex={-1}>
                        <p className="text-sm font-semibold">
                            {t('loyalty.desk.staffSection', 'Your staff')}
                        </p>
                        <p className={hint}>
                            {t('loyalty.desk.staffNote',
                                'Each person gets an email with a link and chooses their own PIN. '
                                + 'A manager can also approve points.')}
                        </p>
                        <div className="mt-2 grid gap-2 sm:grid-cols-4">
                            <Input placeholder={t('loyalty.firstName', 'First name')} value={draft.first_name}
                                   data-testid="cc-desk-new-name"
                                   onChange={(e) => setDraft({ ...draft, first_name: e.target.value })} />
                            <Input placeholder={t('loyalty.email', 'Email')} value={draft.email}
                                   data-testid="cc-desk-new-email"
                                   onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
                            <select className={field} value={draft.role} data-testid="cc-desk-new-role"
                                    onChange={(e) => setDraft({ ...draft, role: e.target.value })}>
                                <option value="staff">{t('loyalty.desk.roleStaff', 'Staff')}</option>
                                <option value="manager">{t('loyalty.desk.roleManager', 'Manager')}</option>
                            </select>
                            <Button size="sm" disabled={working || !draft.email} data-testid="cc-desk-add"
                                    onClick={() => act(async () => {
                                        await api.post('/loyalty/staff', { organization_id: orgId, ...draft });
                                        setDraft({ first_name: '', email: '', role: 'staff' });
                                    })}>
                                {working ? <Loader2 className="h-4 w-4 animate-spin" />
                                    : t('loyalty.desk.invite', 'Invite')}
                            </Button>
                        </div>
                        <ul className="mt-3 space-y-2" data-testid="cc-desk-staff-list">
                            {(row?.staff || []).map((s) => (
                                <li key={s.id} className="flex flex-wrap items-center gap-2 rounded-lg
                                                          border border-slate-200 px-3 py-2 text-xs
                                                          dark:border-slate-700"
                                    data-testid={`cc-desk-staff-${s.id}`}>
                                    <span className="font-medium">{s.first_name || s.email}</span>
                                    <span className="opacity-70">{s.email} · {s.role}</span>
                                    <span className="opacity-70">
                                        {s.registered ? t('loyalty.desk.hasPin', 'PIN set')
                                            : t('loyalty.desk.waitingPin', 'waiting for their PIN')}
                                    </span>
                                    {s.invite_token && (
                                        <>
                                            <span className="break-all text-[10px] opacity-60"
                                                  data-testid={`cc-desk-link-${s.id}`}>
                                                {`${window.location.origin}/loyalty/staff-setup?token=${s.invite_token}`}
                                            </span>
                                            <Button size="sm" variant="outline"
                                                    data-testid={`cc-desk-copy-${s.id}`}
                                                    onClick={() => navigator.clipboard?.writeText(
                                                        `${window.location.origin}/loyalty/staff-setup?token=${s.invite_token}`)}>
                                                <Copy className="h-3 w-3" />
                                                <span className="ml-1">{t('common.copy', 'Copy')}</span>
                                            </Button>
                                        </>
                                    )}
                                    <span className="ml-auto flex flex-wrap items-center gap-2">
                                        {s.active ? (
                                            <Button size="sm" variant="outline" disabled={working}
                                                    data-testid={`cc-desk-pause-${s.id}`}
                                                    onClick={() => act(() => api.patch(
                                                        `/loyalty/staff/${s.id}`, { active: false }))}>
                                                {t('loyalty.desk.pause', 'Pause')}
                                            </Button>
                                        ) : (
                                            <Button size="sm" variant="outline" disabled={working}
                                                    data-testid={`cc-desk-resume-${s.id}`}
                                                    onClick={() => act(() => api.patch(
                                                        `/loyalty/staff/${s.id}`, { active: true }))}>
                                                {t('loyalty.desk.resume', 'Resume')}
                                            </Button>
                                        )}
                                        <Button size="sm" variant="outline"
                                                data-testid={`cc-desk-resend-${s.id}`}
                                                onClick={() => act(() => api.post(`/loyalty/staff/${s.id}/invite`))}>
                                            {t('loyalty.desk.resend', 'Send the link again')}
                                        </Button>
                                        {open === `remove-${s.id}` ? (
                                            <>
                                                <span className={hint}>
                                                    {t('loyalty.desk.removeAsk',
                                                        'They stop signing in. Nothing they did is erased.')}
                                                </span>
                                                <Button size="sm" variant="outline" disabled={working}
                                                        data-testid={`cc-desk-remove-go-${s.id}`}
                                                        onClick={() => act(async () => {
                                                            await api.delete(`/loyalty/staff/${s.id}`);
                                                            setOpen(null);
                                                        })}>
                                                    {t('common.remove', 'Remove')}
                                                </Button>
                                                <Button size="sm" variant="ghost"
                                                        data-testid={`cc-desk-remove-cancel-${s.id}`}
                                                        onClick={() => setOpen(null)}>
                                                    {t('common.cancel', 'Cancel')}
                                                </Button>
                                            </>
                                        ) : (
                                            <Button size="sm" variant="ghost"
                                                    data-testid={`cc-desk-remove-${s.id}`}
                                                    onClick={() => setOpen(`remove-${s.id}`)}>
                                                {t('common.remove', 'Remove')}
                                            </Button>
                                        )}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        {(row?.removed || []).length > 0 && (
                            <div className="mt-3" data-testid="cc-desk-removed-list">
                                <p className="text-xs font-semibold">
                                    {t('loyalty.desk.removedTitle', 'Removed people')}
                                </p>
                                {(row.removed || []).map((s) => (
                                    <p key={s.id} className="mt-1 flex items-center gap-2 text-xs"
                                       data-testid={`cc-desk-removed-${s.id}`}>
                                        <span>{s.first_name || s.email}</span>
                                        <span className="opacity-60">{s.email}</span>
                                        <Button size="sm" variant="outline" className="ml-auto"
                                                disabled={working} data-testid={`cc-desk-restore-${s.id}`}
                                                onClick={() => act(() => api.post(
                                                    `/loyalty/staff/${s.id}/restore`))}>
                                            {t('loyalty.desk.bringBack', 'Bring back')}
                                        </Button>
                                    </p>
                                ))}
                            </div>
                        )}
                    </section>

                    <section className="rounded-xl border border-slate-200 p-3 dark:border-slate-700"
                             data-testid="cc-desk-numbers-section">
                        <p className="text-sm font-semibold">
                            {t('loyalty.desk.numbersSection', 'Points and limits')}
                        </p>
                        <div className="mt-2 grid gap-3 sm:grid-cols-3">
                            <div>
                        <Label className="mb-1 block text-xs">
                            {t('loyalty.desk.perDollar', 'Points per dollar')}
                        </Label>
                        <Input type="number" step="0.01" ref={rateRef}
                               defaultValue={row?.points_per_dollar ?? ''}
                               data-testid="cc-desk-per-dollar"
                               onBlur={(e) => act(async () => {
                                   await onSave({ points_per_dollar: e.target.value });
                                   setSaved('rate');
                                   window.setTimeout(() => setSaved(''), 1600);
                               })} />
                        <p className={hint}>
                            {saved === 'rate' ? t('loyalty.desk.savedNote', 'Saved')
                                : t('loyalty.desk.perDollarNote', 'A $100 bill at 2 gives 200 points.')}
                        </p>
                    </div>
                            <div>
                                <Label className="mb-1 block text-xs">
                                    {t('loyalty.desk.expiryHours', 'How long a visit stays open (hours)')}
                                </Label>
                                <Input type="number" defaultValue={row?.checkin_hours ?? 12}
                                       data-testid="cc-desk-hours"
                                       onBlur={(e) => act(async () => {
                                           await onSave({ desk_checkin_hours: e.target.value });
                                           setSaved('hours');
                                           window.setTimeout(() => setSaved(''), 1600);
                                       })} />
                                <p className={hint}>
                                    {saved === 'hours' ? t('loyalty.desk.savedNote', 'Saved')
                                        : t('loyalty.desk.expiryNote',
                                            'After this, a client who signed in drops off the list.')}
                                </p>
                            </div>
                            <div>
                                <Label className="mb-1 block text-xs">
                                    {t('loyalty.desk.maxBill', 'Biggest bill allowed ($)')}
                                </Label>
                                <Input data-testid="cc-desk-max-bill"
                                       {...moneyFieldProps(maxBill, setMaxBill)}
                                       onBlur={() => { setMaxBill(maxBill ? amountOnly(plain(maxBill)) : '');
                                           act(async () => {
                                               await onSave({ desk_max_bill: plain(maxBill) });
                                               setSaved('bill');
                                               window.setTimeout(() => setSaved(''), 1600);
                                           }); }} />
                                <p className={hint}>
                                    {saved === 'bill' ? t('loyalty.desk.savedNote', 'Saved')
                                        : t('loyalty.desk.maxBillNote',
                                            'Only used when the ceiling below is switched on.')}
                                </p>
                            </div>
                        </div>

                        {/* GIFT CERTIFICATE REFERRALS: a thank-you for the giver,
                            a welcome for her friend. Each one switches off here. */}
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            {[['referral_giver', 'referral_giver_percent',
                               t('loyalty.desk.giverPercent', 'Giver thank-you (% of the certificate)'),
                               t('loyalty.desk.giverNote',
                                 '10% of a $250 certificate is $25 in points for her.')],
                              ['referral_friend', 'referral_friend_percent',
                               t('loyalty.desk.friendPercent', 'Friend welcome (% of the cash she pays)'),
                               t('loyalty.desk.friendNote',
                                 'A $500 bill less a $250 certificate is $250 cash, so $25 in points.')],
                            ].map(([sw, key, label, note]) => (
                                <div key={key}>
                                    <div className="mb-1 flex items-center justify-between gap-2">
                                        <Label className="text-xs">{label}</Label>
                                        <Switch checked={isOn(sw)} label=""
                                                testid={`cc-desk-switch-${sw}`}
                                                onChange={(v) => { setJustSet((s) => ({ ...s, [sw]: v }));
                                                    act(() => onSave({ [sw]: v })); }} />
                                    </div>
                                    <Input type="number" step="0.5" disabled={!isOn(sw)}
                                           defaultValue={cfg?.[key] ?? ''}
                                           data-testid={`cc-desk-${key.replace(/_/g, '-')}`}
                                           onBlur={(e) => act(async () => {
                                               await onSave({ [key]: e.target.value });
                                               setSaved(key);
                                               window.setTimeout(() => setSaved(''), 1600);
                                           })} />
                                    <p className={hint}>
                                        {saved === key ? t('loyalty.desk.savedNote', 'Saved')
                                            : (isOn(sw) ? note
                                                : t('loyalty.desk.offNote', 'Off — no points, the certificate still works.'))}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <Switch checked={isOn('referral_new_only')}
                                label={t('loyalty.desk.newOnly', 'New clients only')}
                                note={t('loyalty.desk.newOnlyNote',
                                    'A certificate can only be used by someone who has not earned '
                                    + 'points here before.')}
                                testid="cc-desk-switch-referral_new_only"
                                onChange={(v) => { setJustSet((s) => ({ ...s, referral_new_only: v }));
                                    act(() => onSave({ referral_new_only: v })); }} />
                    </section>

                    <section className="rounded-xl border border-slate-200 p-3 dark:border-slate-700"
                             data-testid="cc-desk-permissions-section">
                        <p className="text-sm font-semibold">
                            {t('loyalty.desk.permSection', 'What each person may do')}
                        </p>
                        <div className="mt-2 grid gap-3 sm:grid-cols-2">
                            {PERMS.map(([key, label, note]) => (
                                <Switch key={key} testid={`cc-desk-perm-${key}`} checked={perm(key)}
                                        onChange={(v) => setPerm(key, v)} label={label} note={note} />
                            ))}
                        </div>
                    </section>

                    <section className="rounded-xl border border-slate-200 p-3 dark:border-slate-700"
                             data-testid="cc-desk-card-section">
                        <p className="text-sm font-semibold">{t('loyalty.desk.cardSection', 'The card')}</p>
                        {(row?.cards || []).length === 0 ? (
                            <p className="mt-2 text-[11px] text-amber-600" data-testid="cc-desk-no-card">
                                {t('loyalty.desk.noCard', 'No card is set to Org Loyalty Portal yet.')}
                            </p>
                        ) : (row.cards || []).map((c) => (
                            <p key={c} className="mt-2 break-all text-xs" data-testid={`cc-desk-card-${c}`}>
                                {`${window.location.origin}/loyalty-desk/${c}`}
                            </p>
                        ))}
                    </section>
                </div>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        </div>
    );
};

export default DeskPanel;
